import { z } from "zod";
/**
 * The storage contract (block 06), shared by `apps/backend` and
 * `@nook/api-client`.
 *
 * The limits live here rather than in the backend because the drop-zone has to
 * refuse a 25MB file *before* it uploads it, and a client that hard-codes its
 * own copy of 20MB is a client that disagrees with the bucket the day either
 * changes. `@nook/schema` is the single source of truth for the contract, and a
 * threshold both sides enforce is part of one (plan §2).
 */
/** plan §3.6. Enforced at the bucket; pre-checked at sign for a clean error. */
export const MAX_UPLOAD_BYTES = 20_971_520;
/**
 * plan §14.3 — 5GB trialing, 50GB pro.
 *
 * `expired` keeps the trialing allowance: block 20's plan gate is what stops an
 * expired user, and a second, quieter gate here would fail them with the wrong
 * message.
 */
export const STORAGE_LIMIT_BYTES = {
    trialing: 5_368_709_120,
    pro: 53_687_091_200,
    expired: 5_368_709_120,
};
/**
 * `POST /v1/uploads/sign`.
 *
 * `sizeBytes` is unbounded *here* on purpose: over the cap is `413
 * file_too_large`, which is a policy answer with its own code, not a malformed
 * request. The route validates shape; `lib/storage.ts` enforces the limits.
 *
 * There is no `nookId`: the nook does not exist yet on any capture path — every
 * client signs, PUTs and only then posts the capture — so the path's middle
 * segment is minted here instead (override 2).
 *
 * The iOS share extension builds this body by hand
 * (`apps/mobile/targets/share/ShareViewController.swift`): a change here goes there too.
 */
export const signUploadSchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentType: z
        .string()
        .trim()
        .regex(/^image\/[a-z0-9.+-]+$/i, "contentType must be an image/* type"),
    sizeBytes: z.number().int().positive(),
});
/**
 * `expiresAt` is Supabase's, not ours: a signed upload token is valid for two
 * hours and the duration is not configurable, so it is reported rather than
 * requested.
 */
export const signUploadResultSchema = z.object({
    uploadUrl: z.string(),
    storagePath: z.string(),
    expiresAt: z.iso.datetime(),
});
/** `GET /v1/uploads/usage`. Drives the quota line above the drop-zone. */
export const storageUsageSchema = z.object({
    usedBytes: z.number(),
    limitBytes: z.number(),
});
//# sourceMappingURL=storage.js.map